import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdInput } from "src/components/forms/input";
import { PdCheck } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import api from "src/util/apiNew";
import { openUrlInNewTab } from "src/util/linking";
import { useIsMobile } from "../ui";

const ModalContent: React.FC<{ onClose: tModalComponentProps["onClose"] }> = ({ onClose }) => {
  const isMobile = useIsMobile();
  const [shopLink, setShopLink] = React.useState("");
  const [isSubmitted, setSubmitted] = React.useState(false);
  const rShopifySessionId = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.shopifySessionId : null,
  );

  const handleOnClose = () => {
    onClose();
  };

  const handleOnChangeText = (value: string) => {
    setShopLink(value);
  };

  const handleOnConnect = async () => {
    const response = await api.get<string>(`shopify/${shopLink}/auth`);
    openUrlInNewTab(response.data);
    setSubmitted(true);
  };

  if (isSubmitted && rShopifySessionId) {
    return (
      <>
        <View style={styles.alignCenter}>
          <PdCheck color={kColors.green} style={[styles.heightLg, styles.mb10]} />
          <PdModalTitle style={styles.mb4}>Success!</PdModalTitle>
          <PdText style={[styles.textCenter, styles.mb4]}>
            Your Shopify store is now integrated with The Main Tab. Please click &apos;Shopify Settings&apos; on the top
            right to import your catalog, sync orders and manage inventory.
          </PdText>
        </View>
      </>
    );
  }

  return (
    <>
      <PdModalTitle style={styles.mb5}>Connect your Shopify Account</PdModalTitle>
      <PdText style={styles.mb4}>
        Please enter your &quot;myshopify.com&quot; store address to begin the account linking process.
      </PdText>
      <View style={[styles.pdInputGroup, styles.mb10]}>
        <PdInput style={[styles.widthFull, styles.border0]} value={shopLink} onChangeText={handleOnChangeText} />
        <PdText style={[styles.textGray, styles.mr2]}>.myshopify.com</PdText>
      </View>
      <PdStack
        isReversed={isMobile}
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[1, 1]}
        gap={3}
        style={styles.wFull}
      >
        <PdButton colorScheme="purple" variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton onPress={handleOnConnect}>
          <PdButtonLabel>Connect</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const VendorSetupShopifyModal: React.FC<tModalComponentProps> = ({ onClose }) => {
  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal style={styles.modal} onClose={onClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <ModalContent onClose={onClose} />
      </ScrollView>
    </PdModal>
  );
};

const useVendorSetupShopifyModal = (): tUseModal => {
  return useModal(VendorSetupShopifyModal);
};

export default useVendorSetupShopifyModal;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  border0: {
    borderWidth: 0,
  },
  heightLg: {
    height: 50,
  },
  mb10: {
    marginBottom: kStyleConst.kSpacing * 10,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  textCenter: {
    textAlign: "center",
  },
  textGray: {
    color: kColors.gray500,
  },
  wFull: {
    width: "100%",
  },
  widthFull: {
    width: "100%",
  },
});
